import { authSvcAxiosInstance } from "@/constants";
import { authServiceEndpoints } from "@/constants/endpoints";
import { Team, TeamMember } from "@/types/team.types";
import { IUser } from "@/types/user.types";

/**
 * Fetches user data for given user IDs in a single batch request.
 * One request per member.
 */
const fetchUsersByIds = async (userIds: string[]): Promise<Record<string, IUser>> => {
    if (userIds.length === 0) return {};

    try {
        const response = await authSvcAxiosInstance.post<IUser[]>(authServiceEndpoints.getUsersByIds, {
            userIds,
        });

        const usersMap: Record<string, IUser> = {};
        response.data.forEach((user) => {
            usersMap[user.id] = user;
        });

        return usersMap;
    } catch {
        // On failure, return an empty map so callers fall back to existing member data
        return {};
    }
};

/**
 * Enriches teams with missing user data by fetching user details for members
 * that don't have complete user information
 *
 * @param teams - Array of teams or single team to enrich
 * @returns Enriched teams with complete user data
 */
export const enrichTeamsWithUsers = async (teams: Team[] | Team): Promise<Team[] | Team> => {
    // Normalize to array
    const teamsArray = Array.isArray(teams) ? teams : [teams];

    // Collect all member IDs that are missing user data
    const memberIdsMissingUserData: string[] = [];
    teamsArray.forEach((team) => {
        team.members.forEach((member) => {
            if (!member.user?.name && !memberIdsMissingUserData.includes(member.id)) {
                memberIdsMissingUserData.push(member.id);
            }
        });
    });

    // Fetch missing user data
    const fetchedUsersMap = await fetchUsersByIds(memberIdsMissingUserData);

    // Enrich teams with fetched user data
    const enrichedTeams = teamsArray.map((team) => ({
        ...team,
        members: team.members.map((member): TeamMember => {
            // Use fetched user data if available, otherwise fall back to team member user data
            const userData = fetchedUsersMap[member.id] || member.user;
            return {
                ...member,
                user: userData || member.user,
            };
        }),
    }));

    // Return single team if input was single team, otherwise return array
    return Array.isArray(teams) ? enrichedTeams : enrichedTeams[0];
};
